import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'

const styles = () => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(210px, 320px)',
    gap: 'var(--careco-space-xl, 24px)',
    width: '100%',
    marginBottom: 'var(--careco-space-xl, 24px)',
  },
  gridMulti: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  },
  tile: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--careco-space-md, 16px)',
    boxSizing: 'border-box',
    padding: 'var(--careco-space-lg, 20px)',
    borderRadius: 'var(--careco-rounded-lg, 20px)',
    background: 'var(--careco-surface)',
    border: '1px solid var(--careco-border)',
    boxShadow: 'var(--careco-shadow-card, 0 2px 10px 2px rgba(110,110,110,0.06))',
    fontFamily: 'var(--careco-font)',
    transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--careco-shadow-navigation, 0 4px 4px 3px rgba(110,110,110,0.06))',
      borderColor: 'var(--careco-primary-light)',
    },
  },
  iconCircle: {
    width: 44,
    height: 44,
    flexShrink: 0,
    borderRadius: 'var(--careco-rounded-pill, 999px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  value: {
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1,
    color: 'var(--careco-text-primary)',
    fontVariantNumeric: 'tabular-nums',
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.3,
    color: 'var(--careco-text-secondary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  hint: {
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.3,
    color: 'var(--careco-text-muted)',
    fontVariantNumeric: 'tabular-nums',
  },
  '@media (max-width: 600px)': {
    grid: {
      gridTemplateColumns: '1fr',
    },
    gridMulti: {
      gridTemplateColumns: '1fr',
    },
  },
})

const Glyph = ({ path }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={path} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

Glyph.propTypes = { path: PropTypes.string }

const ICON = {
  building:
    'M4 21h16M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16M14 9h4a1 1 0 0 1 1 1v11M9 8h1M9 12h1M9 16h1',
  active: 'M22 11.1V12a10 10 0 1 1-5.9-9.1M22 4L12 14.01l-3-3',
  package: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.3 7L12 12l8.7-5M12 22V12',
}

/**
 * Estate summary above the organisations list.
 * CareBrain: organisation count only.
 * QCS: also active organisations + packages in use when those props are numbers.
 */
const OrganisationsOverview = ({
  classes,
  total,
  activeOrganizations,
  packagesInUse,
  loading,
}) => {
  const showQcsExtras =
    typeof activeOrganizations === 'number' || typeof packagesInUse === 'number'

  const formatValue = value => {
    if (loading || typeof value !== 'number') return '—'
    return value.toLocaleString()
  }

  const tiles = [
    {
      key: 'total',
      icon: ICON.building,
      value: total,
      label:
        !loading && typeof total === 'number' && total === 1
          ? 'Organisation'
          : 'Organisations',
      hint: 'across your account',
    },
  ]

  if (showQcsExtras) {
    tiles.push(
      {
        key: 'active',
        icon: ICON.active,
        value: activeOrganizations,
        label: 'Active organisations',
        hint:
          typeof total === 'number' ? `of ${total.toLocaleString()}` : 'currently activated',
      },
      {
        key: 'packages',
        icon: ICON.package,
        value: packagesInUse,
        label: 'Packages in use',
        hint: 'across your account',
      },
    )
  }

  return (
    <div className={`${classes.grid} ${showQcsExtras ? classes.gridMulti : ''}`}>
      {tiles.map(tile => (
        <div className={classes.tile} key={tile.key}>
          <span className={classes.iconCircle}>
            <Glyph path={tile.icon} />
          </span>
          <span className={classes.body}>
            <span className={classes.value}>{formatValue(tile.value)}</span>
            <span className={classes.label}>{tile.label}</span>
            {tile.hint && <span className={classes.hint}>{tile.hint}</span>}
          </span>
        </div>
      ))}
    </div>
  )
}

OrganisationsOverview.propTypes = {
  classes: PropTypes.object,
  total: PropTypes.number,
  activeOrganizations: PropTypes.number,
  packagesInUse: PropTypes.number,
  loading: PropTypes.bool,
}

export default withStyles(styles)(OrganisationsOverview)
